import { InputLabel, Stack, useMediaQuery, useTheme } from "@mui/material";
import { emailValidation, phoneNumberValidation } from "models/constants";
import { useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormTextField } from "UI/FormTextField";
import { required } from "utils";

const DEBOUNCE_TIME_FOR_EMAIL_CHECK_IN_MS = 300;

export const NewSenderForm = ({
  originator,
  control,
  watch,
  loading,
  setValue,
  setFormError,
  clearErrors,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isPracticeSelected =
    !!watch("practiceName") || !!watch("dashboardAccessToken");
  const email = watch("email");
  const isPracticePhoneEntered =
    watch("practicePhone")?.replace(/_/g, "")?.length === 12;

  const checkIfSenderExists = async (email) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/public/sender/exists-by-email/${email}`,
        {
          method: "GET",
        }
      );
      const result = await response.json();

      /* TODO - uncomment this error handling */

      // if (response?.status !== 200 || result?.error) {
      //   throw new Error(
      //     result?.message ?? result?.error ?? "Something went wrong"
      //   );
      // }

      if (result?.value) {
        setFormError("email", {
          type: "custom",
          message:
            "Sender with this email already has a Referral Stream Account. Please log in instead.",
        });
      } else {
        clearErrors("email");
      }
    } catch (error) {
      console.error(error);
      // setError(error?.message ?? error?.error ?? "Something went wrong");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheckIfSenderExists = useCallback(
    debounce((email) => {
      checkIfSenderExists(email);
    }, DEBOUNCE_TIME_FOR_EMAIL_CHECK_IN_MS),
    []
  );

  const isEmailDisabled = !!originator || loading || !isPracticePhoneEntered;

  const isDisabled =
    !!originator || loading || !isPracticeSelected;

  useEffect(() => {
    if (email && isPracticeSelected) {
      debouncedCheckIfSenderExists(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isPracticeSelected]);

  useEffect(() => {
    if (isPracticePhoneEntered && !originator?.id) {
      setValue("senderId", "", { shouldDirty: true });
      setValue("firstName", "", { shouldDirty: true });
      setValue("lastName", "", { shouldDirty: true });
      setValue("phone", "", { shouldDirty: true });
      setValue("email", "", { shouldDirty: true });
      setValue("jobTitle", "", { shouldDirty: true });
    }
  }, [isPracticePhoneEntered, setValue, originator]);

  return (
    <Stack flex={1} gap={1}>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        gap={2}
      >
        <Stack flex={1} direction={"column"} alignItems={"flex-start"}>
          <InputLabel htmlFor="email">Email</InputLabel>
          <FormTextField
            id="email"
            name="email"
            control={control}
            rules={emailValidation}
            InputProps={{
              autoComplete: "off",
              disabled: isEmailDisabled,
            }}
          />
        </Stack>
        <Stack alignItems={"flex-start"}>
          <InputLabel htmlFor="phone">Phone</InputLabel>
          <FormMaskedTextField
            id="phone"
            name="phone"
            control={control}
            rules={phoneNumberValidation}
            mask="999-999-9999"
            InputProps={{
              autoComplete: "off",
              disabled: isDisabled,
            }}
          />
        </Stack>
      </Stack>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        gap={2}
      >
        <Stack direction={"column"} alignItems={"flex-start"}>
          <InputLabel htmlFor="firstName">First Name</InputLabel>
          <FormTextField
            id="firstName"
            name="firstName"
            control={control}
            rules={{ required }}
            InputProps={{
              autoComplete: "off",
              disabled: isDisabled,
            }}
          />
        </Stack>
        <Stack flex={1} direction={"column"} alignItems={"flex-start"}>
          <InputLabel htmlFor="lastName">Last Name</InputLabel>
          <FormTextField
            id="lastName"
            name="lastName"
            control={control}
            rules={{ required }}
            InputProps={{
              autoComplete: "off",
              disabled: isDisabled,
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
